"use client"

import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { contactFormSchema, ContactFormValues } from '@/schemas/contact-schema'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { generateCaptchaChallenge } from '@/lib/spam-protection'
import { useRouter } from 'next/navigation'

const ContactForm = () => {
  const router = useRouter()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [captchaQuestion, setCaptchaQuestion] = useState<{ num1: number; num2: number } | null>(null)

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      phone: '',
      email: '',
      message: '',
      captcha: undefined,
      captchaToken: '',
      hp_website: '',
      formStartTime: undefined,
    },
  })

  // Generate or refresh captcha challenge on client side
  const refreshCaptcha = useCallback(() => {
    const challenge = generateCaptchaChallenge()
    setCaptchaQuestion({ num1: challenge.num1, num2: challenge.num2 })
    form.setValue('captchaToken', challenge.token, { shouldValidate: false })
    form.setValue('captcha', undefined as unknown as number, { shouldValidate: false })
  }, [form])

  useEffect(() => {
    refreshCaptcha()
    form.setValue('formStartTime', Date.now(), { shouldValidate: false })
  }, [refreshCaptcha, form])

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true)
    setError(null)
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      
      const result = await response.json()
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to send message')
      }
      
      router.push('/thank-you?type=contact')
      
    } catch (err: any) {
      console.error('Form submission error:', err)
      setError(err.message || 'Something went wrong. Please try again.')
      // Refresh captcha on failure
      refreshCaptcha()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="border-0 shadow-xl py-0">
      <CardContent className="p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Honeypot field (hidden from legitimate users, catches spam bots) */}
            <div 
              aria-hidden="true" 
              tabIndex={-1} 
              style={{ display: 'none', position: 'absolute', left: '-9999px', opacity: 0 }}
            >
              <label htmlFor="hp_website_contact">Leave this field blank</label>
              <input
                id="hp_website_contact"
                type="text"
                {...form.register('hp_website')}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name Field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Full Name *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="" 
                        {...field} 
                        className="h-11"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Phone Field */}
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700">Phone Number *</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="" 
                        {...field} 
                        className="h-11"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Email Address *</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="" 
                      type="email"
                      {...field} 
                      className="h-11"
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Message Field */}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700">Your Message *</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder=""
                      className="min-h-[120px] resize-none"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Captcha Field */}
            <FormField
              control={form.control}
              name="captcha"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-gray-700">
                      Security Check: What is {captchaQuestion ? `${captchaQuestion.num1} + ${captchaQuestion.num2}` : '...'}? *
                    </FormLabel>
                    <button
                      type="button"
                      onClick={refreshCaptcha}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer transition-colors"
                      title="Get a new math problem"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      <span>Refresh</span>
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <FormControl>
                        <Input 
                          placeholder="Enter the sum"
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value
                            field.onChange(val === '' ? undefined : parseInt(val, 10))
                          }}
                          className="h-11"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                    </div>
                    {captchaQuestion && (
                      <div className="px-4 py-2.5 bg-gray-100 border border-gray-200 rounded-md font-mono text-base font-semibold text-gray-700 whitespace-nowrap select-none">
                        {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full h-12 text-lg bg-[#46473f] hover:bg-[#363730] text-white cursor-pointer transition-colors"
              disabled={isSubmitting}
              aria-label="Send Message"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Sending Message...
                </>
              ) : (
                'Send Message'
              )}
            </Button>

            <p className="text-xs text-gray-500 text-center">
              By submitting this form, you agree to our privacy policy.
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

export default ContactForm